import { SEO, BreadcrumbSchema, SpeakableSchema, FaqSchema } from '@/components/SEO';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { CTABanner } from '@/components/CTABanner';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  MapPin, CheckCircle2, ArrowRight, Shield, Clock, Phone, Users, Hammer, Mail
} from 'lucide-react';

/* ─── County data ─────────────────────────────────── */
interface County {
  name: string;
  short: string;
  path: string;
  tagline: string;
  towns: string[];
  founded: string;
  projectsBuilt: string;
}

const counties: County[] = [
  {
    name: 'Morris County',
    short: 'Morris',
    path: '/areas/morris-county',
    tagline: 'Our home base since 1999',
    towns: ['Morristown', 'Parsippany', 'Denville', 'Randolph', 'Roxbury', 'Chester', 'Madison', 'Chatham', 'Boonton', 'Dover'],
    founded: '1999',
    projectsBuilt: '350+',
  },
  {
    name: 'Sussex County',
    short: 'Sussex',
    path: '/areas/sussex-county',
    tagline: 'Lake-country decks built right',
    towns: ['Newton', 'Sparta', 'Vernon', 'Hopatcong', 'Andover', 'Byram', 'Frankford', 'Hampton', 'Stillwater', 'Green'],
    founded: '2003',
    projectsBuilt: '200+',
  },
  {
    name: 'Warren County',
    short: 'Warren',
    path: '/areas/warren-county',
    tagline: 'Western NJ craftsmanship',
    towns: ['Belvidere', 'Hackettstown', 'Phillipsburg', 'Washington', 'Blairstown', 'Knowlton', 'Oxford', 'Allamuchy', 'Independence', 'Lopatcong'],
    founded: '2005',
    projectsBuilt: '150+',
  },
  {
    name: 'Hunterdon County',
    short: 'Hunterdon',
    path: '/areas/hunterdon-county',
    tagline: 'Historic homes · Modern decks',
    towns: ['Flemington', 'Clinton', 'Lambertville', 'Raritan', 'Readington', 'Tewksbury', 'Bethlehem', 'Alexandria', 'Delaware', 'Union'],
    founded: '2007',
    projectsBuilt: '150+',
  },
  {
    name: 'Somerset County',
    short: 'Somerset',
    path: '/areas/somerset-county',
    tagline: 'Premium builds in central NJ',
    towns: ['Somerville', 'Bridgewater', 'Bernardsville', 'Bound Brook', 'Franklin', 'Hillsborough', 'Warren', 'Watchung', 'Bedminster', 'Branchburg'],
    founded: '2010',
    projectsBuilt: '150+',
  },
];

/* ─── Hero ─────────────────────────────────────────── */
const PageHero = () => (
  <section className="relative pt-28 pb-14 bg-navy text-white overflow-hidden">
    <div className="container mx-auto px-4 relative z-10">
      <div className="text-center max-w-4xl mx-auto">
        <p className="section-eyebrow justify-center">Where We Build</p>
        <h1
          className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase leading-[1.05]"
          data-speakable
        >
          NJ Service <span className="text-lime">Areas</span>
        </h1>
        <p
          className="text-base md:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed font-body"
          data-speakable
        >
          DeckPros NJ proudly builds custom decks across five New Jersey counties — Morris, Sussex, Warren, Hunterdon, and Somerset — plus every town in between.
        </p>
      </div>
    </div>
  </section>
);

/* ─── Counties grid ────────────────────────────────── */
const CountiesList = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className="section-compact bg-cream">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="section-eyebrow justify-center">Counties We Serve</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
            5 Counties · <span className="text-lime">50+ Towns</span>
          </h2>
          <p className="text-stone font-body">
            Click any county for detailed local information, permit help, and recent projects.
          </p>
        </div>

        <div
          ref={ref}
          itemScope
          itemType="https://schema.org/ItemList"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-7xl mx-auto"
        >
          {counties.map((county, i) => (
            <motion.article
              key={county.name}
              itemScope
              itemType="https://schema.org/Place"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.08 }}
              className="group bg-white border-2 border-navy/10 hover:border-lime hover:-translate-y-1 hover:shadow-hard transition-all duration-200 flex flex-col"
            >
              <div className="h-1.5 bg-lime" />
              <div className="p-6 md:p-7 flex flex-col flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 bg-lime flex items-center justify-center border-2 border-navy flex-shrink-0">
                      <MapPin className="w-5 h-5 text-navy" />
                    </div>
                    <div>
                      <h3 itemProp="name" className="font-display text-lg font-bold text-navy uppercase tracking-wide group-hover:text-lime transition-colors">
                        {county.name}
                      </h3>
                      <p className="text-[11px] font-display font-bold uppercase tracking-[0.15em] text-lime">
                        Since {county.founded}
                      </p>
                    </div>
                  </div>
                  <span className="font-display text-xs font-bold uppercase tracking-wider text-stone/50">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>

                <p className="text-stone text-sm font-body italic mb-4">
                  "{county.tagline}"
                </p>

                <div className="flex items-center gap-4 mb-4 text-xs">
                  <span className="flex items-center gap-1.5 bg-cream border border-navy/15 px-2.5 py-1 font-display font-bold text-navy uppercase tracking-wider">
                    <Hammer className="w-3 h-3 text-lime" />
                    {county.projectsBuilt} builds
                  </span>
                </div>

                <p className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-stone mb-2">
                  Towns in {county.short} County
                </p>
                <ul className="grid grid-cols-2 gap-1 mb-5 flex-1">
                  {county.towns.slice(0, 8).map((town) => (
                    <li key={town}>
                      <Link
                        to={`/areas/${town.toLowerCase().replace(/\s+/g, '-')}`}
                        className="flex items-center gap-1.5 text-[13px] text-navy/85 hover:text-lime font-body transition-colors"
                      >
                        <CheckCircle2 className="w-3 h-3 text-lime flex-shrink-0" />
                        {town}
                      </Link>
                    </li>
                  ))}
                </ul>

                <Link to={county.path} className="mt-auto">
                  <Button variant="cta" size="default" className="w-full h-10 text-xs">
                    View {county.short} County
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Why Local ──────────────────────────────────── */
const WhyLocal = () => {
  const reasons = [
    {
      icon: Shield,
      title: 'Licensed Across NJ',
      description: 'Fully licensed, bonded, and insured in every county we serve. We handle all permits, HOA paperwork, and code inspections.',
    },
    {
      icon: Clock,
      title: 'Fast Response',
      description: 'Local means fast. On-site estimates within 48 hours, project starts typically within 2–4 weeks of signed contract.',
    },
    {
      icon: MapPin,
      title: 'We Know Your Town',
      description: 'We know local building codes, HOA rules, soil conditions, and climate patterns that affect deck construction in your NJ town.',
    },
    {
      icon: Phone,
      title: 'Always Reachable',
      description: 'Post-project questions? Warranty work? Maintenance? Call (201) 657-0855 — we\'re still here, because we\'re local.',
    },
  ];

  return (
    <section className="section-compact bg-navy text-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="section-eyebrow justify-center">Why Local Matters</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold">
            Your Neighborhood <span className="text-lime">Deck Experts</span>
          </h2>
          <p className="text-white/70 mt-3 font-body">
            Being local isn't marketing — it's why we can deliver better quality, faster response, and deeper knowledge of your town.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
          {reasons.map(({ icon: Icon, title, description }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="relative bg-navy-deep border-2 border-white/10 hover:border-lime transition-colors p-6"
            >
              <div className="w-11 h-11 bg-lime flex items-center justify-center border-2 border-navy mb-4">
                <Icon className="w-5 h-5 text-navy" />
              </div>
              <h3 className="font-display text-base font-bold text-white uppercase tracking-wide mb-2">
                {title}
              </h3>
              <p className="text-white/70 text-sm leading-relaxed font-body">
                {description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Coverage Map ───────────────────────────── */
const CoverageMap = () => (
  <section className="section-compact bg-cream-dark">
    <div className="container mx-auto px-4">
      <div className="text-center mb-8 max-w-2xl mx-auto">
        <p className="section-eyebrow justify-center">Coverage Area</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
          From <span className="text-lime">Sussex to Somerset</span>
        </h2>
        <p className="text-stone font-body">
          Our service area covers northwestern and central New Jersey.
        </p>
      </div>

      <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-5">
        <div className="md:col-span-2">
          <div className="border-4 border-navy shadow-hard overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d387191.0361427764!2d-74.82668744831498!3d40.69714942942178!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c0fb959e00409f%3A0x2cd27b07f83f6d8d!2sNew%20Jersey%2C%20USA!5e0!3m2!1sen!2sus!4v1711900000000"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              title="DeckPros NJ Service Area — Morris, Sussex, Warren, Hunterdon & Somerset Counties"
            />
          </div>
        </div>

        <div className="space-y-3">
          <div className="bg-navy text-white p-5 border-2 border-navy shadow-hard">
            <p className="section-eyebrow" style={{ color: 'hsl(var(--lime))' }}>Not Sure?</p>
            <h3 className="font-display text-lg font-bold uppercase tracking-wide mb-2">
              Call to <span className="text-lime">Confirm</span>
            </h3>
            <p className="text-white/75 text-sm font-body mb-4">
              Not sure if we service your town? We often build in towns not listed. Give us a call.
            </p>
            <a href="tel:2016570855">
              <Button variant="cta" size="default" className="w-full">
                <Phone className="w-4 h-4" />
                (201) 657-0855
              </Button>
            </a>
          </div>

          <div className="bg-white border-2 border-navy/15 p-5">
            <p className="section-eyebrow">Prefer Email?</p>
            <a
              href="mailto:support@allcompleteconstruction.com"
              className="font-display text-sm font-bold text-navy hover:text-lime transition-colors flex items-center gap-2 mt-2 break-all"
            >
              <Mail className="w-4 h-4 text-lime flex-shrink-0" />
              support@allcompleteconstruction.com
            </a>
          </div>

          <div className="bg-white border-2 border-navy/15 p-5">
            <p className="section-eyebrow">Business Hours</p>
            <div className="mt-2 text-sm font-body text-stone leading-relaxed">
              <p><strong className="text-navy">Mon–Fri:</strong> 7 AM – 6 PM</p>
              <p><strong className="text-navy">Saturday:</strong> 8 AM – 4 PM</p>
              <p><strong className="text-navy">Sunday:</strong> Closed</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ─── FAQ (AEO / GEO) ────────────────────────── */
const areasFaqs = [
  {
    question: 'What NJ counties does DeckPros NJ serve?',
    answer: 'DeckPros NJ serves five New Jersey counties: Morris County, Sussex County, Warren County, Hunterdon County, and Somerset County. We also build in surrounding towns across northern and central NJ.',
  },
  {
    question: 'Does DeckPros NJ build decks in Morristown, Sparta, or Bridgewater?',
    answer: 'Yes. DeckPros NJ builds in Morristown, Parsippany, Denville, Randolph, and Chester (Morris County); Newton, Sparta, Vernon, and Hopatcong (Sussex County); Somerville, Bridgewater, Bernardsville, and Hillsborough (Somerset County); plus over 50 other NJ towns.',
  },
  {
    question: 'How do I know if DeckPros NJ services my town?',
    answer: 'Call (201) 657-0855 or email support@allcompleteconstruction.com to confirm. We serve over 50 towns across five NJ counties, and often build in smaller towns not explicitly listed on our website.',
  },
  {
    question: 'Does DeckPros NJ handle permits in every NJ town?',
    answer: 'Yes. DeckPros NJ is familiar with permit requirements in every town we serve and handles all building permits, HOA paperwork, and inspection scheduling as part of every project.',
  },
  {
    question: 'Are response times the same across all service areas?',
    answer: 'Yes. DeckPros NJ provides on-site estimates within 48 hours and project start times within 2–4 weeks of contract signing, regardless of which NJ county you\'re in.',
  },
  {
    question: 'Where is DeckPros NJ based?',
    answer: 'DeckPros NJ is based in Morris County, New Jersey. We have been operating from the Morristown area since 1999 and have expanded to serve Sussex, Warren, Hunterdon, and Somerset Counties over 25 years.',
  },
];

const FAQSection = () => (
  <section className="section-compact bg-cream">
    <div className="container mx-auto px-4">
      <div className="text-center mb-10 max-w-2xl mx-auto">
        <p className="section-eyebrow justify-center">Local Service Questions</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
          Service Area <span className="text-lime">FAQs</span>
        </h2>
      </div>

      <div
        className="max-w-4xl mx-auto space-y-3"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        {areasFaqs.map((faq, i) => (
          <details
            key={i}
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
            className="group bg-white border-2 border-navy/15 hover:border-lime transition-colors [&[open]]:border-lime [&[open]]:shadow-hard"
          >
            <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none">
              <h3
                itemProp="name"
                className="font-display text-sm md:text-base font-bold text-navy uppercase tracking-wide group-open:text-lime transition-colors"
              >
                {faq.question}
              </h3>
              <span className="font-display text-2xl text-lime leading-none transition-transform group-open:rotate-45 flex-shrink-0">+</span>
            </summary>
            <div
              itemScope
              itemProp="acceptedAnswer"
              itemType="https://schema.org/Answer"
              className="px-5 pb-5 -mt-1"
            >
              <p itemProp="text" className="text-stone text-sm leading-relaxed font-body border-t border-navy/10 pt-4">
                {faq.answer}
              </p>
            </div>
          </details>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Service-Area Schema ────────────────────────── */
const AreaServedSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': 'https://deckprosnj.com/#organization',
    name: 'DeckPros NJ',
    telephone: '+1-201-657-0855',
    url: 'https://deckprosnj.com/areas',
    address: { '@type': 'PostalAddress', addressRegion: 'NJ', addressCountry: 'US' },
    areaServed: counties.map((c) => ({
      '@type': 'AdministrativeArea',
      name: c.name,
      containedInPlace: { '@type': 'State', name: 'New Jersey' },
      address: { '@type': 'PostalAddress', addressRegion: 'NJ', addressCountry: 'US' },
    })),
  };

  const itemList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'DeckPros NJ Service Counties',
    itemListElement: counties.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Place',
        name: c.name,
        url: `https://deckprosnj.com${c.path}`,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <script type="application/ld+json">{JSON.stringify(itemList)}</script>
    </Helmet>
  );
};

/* ─── Page ────────────────────────────────────────── */
const Areas = () => {
  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Service Areas · NJ Counties"
        description="DeckPros NJ serves Morris, Sussex, Warren, Hunterdon & Somerset Counties in New Jersey. 50+ towns, 1,000+ decks built, 25+ years local experience. Free estimates: (201) 657-0855."
        canonical="/areas"
        keywords="deck builder Morris County NJ, deck builder Sussex County, Warren County deck contractor, Hunterdon County deck, Somerset County deck builder, NJ deck service areas, local deck contractor New Jersey"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: '/' },
        { name: 'Areas', url: '/areas' },
      ]} />
      <SpeakableSchema />
      <FaqSchema faqs={areasFaqs} />
      <AreaServedSchema />

      <Header />
      <main>
        <PageHero />
        <CountiesList />
        <WhyLocal />
        <CoverageMap />
        <FAQSection />
        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default Areas;
